import React from 'react';
import Link from 'next/link';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useClintMediaQuery } from '@/utils/hooks';
import {
  ABOUT_ME_EN,
  EDUCATION_EN,
  OVERVIEW_EN,
  PROJECT_EXPERIENCE_EN,
  WORK_EXPERIENCE_EN,
} from '@/components/resume/resume-builder/resume-advice';
import {
  CANDIDATES_ME_ROUTER_PATH,
  LOGIN_ROUTER_PATH,
} from '@/constants/router-path';
import { QUERY_REDIRECT, QUERY_VALUE } from '@/constants/auth';
import { trackClickEvent } from '@/utils/site-track';

const ADVICE_MENU = [
  {
    title: '概览',
    en: OVERVIEW_EN,
  },
  {
    title: '关于我',
    en: ABOUT_ME_EN,
  },
  {
    title: '教育经历',
    en: EDUCATION_EN,
  },
  {
    title: '工作经历',
    en: WORK_EXPERIENCE_EN,
  },
  {
    title: '项目经历',
    en: PROJECT_EXPERIENCE_EN,
  },
];

const AdviceSection = (props: {
  title: string;
  content: React.ReactNode;
  sectionGroup: string;
  sectionIndex: number;
}) => {
  const { title, content, sectionGroup, sectionIndex } = props;

  return (
    <Link
      className="block relative w-full h-[210px] text-[#2d2d2d] text-sm tablet:w-[400px]"
      href={{
        pathname: LOGIN_ROUTER_PATH,
        query: {
          [QUERY_REDIRECT]: CANDIDATES_ME_ROUTER_PATH,
          [QUERY_VALUE]: `resume-advice_${sectionGroup}_${sectionIndex}`,
        },
      }}
      data-ele-group="resume-advice_content"
      data-ele-id={`${sectionGroup}_${title}`}
      onClick={(e) => trackClickEvent(e, true)}
    >
      <div className="absolute left-0 w-full group px-5 py-8 rounded-2.5xl bg-slate-100 transition-all duration-300 hover:[background:linear-gradient(180deg,#e1e5ff_0%,#cdf0ff_100%)]">
        <p className="h-16 text-xl font-semibold">{title}</p>
        <div className="h-12 line-clamp-5 overflow-hidden transition-all duration-300 group-hover:h-[114px]">
          {content}
        </div>
        <div className="flex justify-end font-semibold mt-2">
          更多详情
          <ArrowForwardRoundedIcon className="ml-5" />
        </div>
      </div>
    </Link>
  );
};

const OverViewAdvice = (props: { hidden: boolean }) => {
  const { hidden } = props;

  return (
    <div hidden={hidden}>
      <div className="flex justify-center gap-4 mb-5 flex-col tablet:flex-row tablet:gap-5 tablet:mb-25">
        <AdviceSection
          title="缺少实习经验的应届生应该如何写简历？"
          content={
            <ul className="m-0 list-none">
              <li>
                <strong>应届生的核心问题：</strong>
                对于应届生而言，缺少实习经验并不是本质问题，本质问题是在简历上缺少足够的信息量以让用人单位对你产生了解和正面印象。
              </li>
              <li>
                <strong>用人单位对职场新人的期望：</strong>
                <ul className="!ml-5 list-disc">
                  <li>有对这个岗位的热情；</li>
                  <li>有学习能力和意愿度；</li>
                </ul>
              </li>
            </ul>
          }
          sectionGroup={OVERVIEW_EN}
          sectionIndex={1}
        />
        <AdviceSection
          title="什么才算是一封优秀的简历？"
          content={
            <>
              <p>
                青铜段位：简历包含必要的信息，信息规范，没有明显的格式紊乱或错误。
              </p>
              <ul className="ml-5 list-disc">
                <li>
                  <strong>排版整洁：</strong>
                  使用统一的字体、合适的项目符号和适当的缩进。
                </li>
                <li>
                  <strong>内容清晰有序：</strong>
                  正确地对模块进行排序，比如个人信息、工作经历、教育背景、技能等。
                </li>
              </ul>
            </>
          }
          sectionGroup={OVERVIEW_EN}
          sectionIndex={2}
        />
        <AdviceSection
          title="简历必须包含哪些核心内容？"
          content={
            <>
              <ul className="list-disc ml-5">
                <li>
                  <strong>必备内容：</strong>个人信息、教育经历、工作经历。
                </li>
                <li>
                  <strong>可选内容：</strong>
                  包括关于我、项目经历、技能和工具、作品集、证书和奖项、校园经历以及公益活动、兴趣爱好。
                </li>
              </ul>
              <p className="text-black/65 mb-1">具体模块：</p>
              <p className="ml-5">
                <strong>关于我</strong>
              </p>
              <li>
                此模块做为你职业脉络的概览，在此处强调突出你的
                <strong>职业定位</strong>，以及过往围绕该职业定位所搭建的
                <strong>核心技能</strong>
                。最后告知你<strong>期待开展工作的方向</strong>。
              </li>
            </>
          }
          sectionGroup={OVERVIEW_EN}
          sectionIndex={3}
        />
      </div>
      <div className="flex justify-center gap-4 flex-col tablet:flex-row tablet:gap-5">
        <AdviceSection
          title="简历的最佳长度：几页最合适？"
          content={
            <ul className="list-disc ml-5">
              <li>
                <strong>以常见的A4纸简历为例：</strong>
                <ul className="!ml-5 list-disc">
                  <li>
                    职业经历比较丰富的求职者：2-3页都没问题，但尽量不要超过3页；
                  </li>
                  <li>
                    职业经历特别丰富的求职者：可以扩展到3页以上，但这种一般是有大量的作品、文献需要被呈现。尽量不要因为在工作经历、项目经历等模块上投入了太多的笔墨而使简历超过3页；
                  </li>
                </ul>
              </li>
            </ul>
          }
          sectionGroup={OVERVIEW_EN}
          sectionIndex={4}
        />
        <AdviceSection
          title="为什么简历不必严格限制在一页内？"
          content={
            <ul className="list-disc ml-5">
              <li>
                <strong>源自特定行业的惯例：</strong>
                将简历内容限制在一页纸内的规范主要源自金融和大型快消行业的校园招聘。这些行业通常面对大量的优秀人才申请，因此采用这种方式便于快速筛选。
              </li>
              <li>
                <strong>一页不适用于大多数行业：</strong>
                虽然这一规范曾被其他一些强势行业采用，但对于大多数常规行业而言，并不适用。大多数用人单位和人力资源部门更希望看到完整、丰富的信息，以全面了解应聘者的职业形象。
              </li>
            </ul>
          }
          sectionGroup={OVERVIEW_EN}
          sectionIndex={5}
        />
      </div>
    </div>
  );
};

const AboutMeAdvice = (props: { hidden: boolean }) => {
  const { hidden } = props;

  return (
    <div hidden={hidden}>
      <div className="flex justify-center gap-4 mb-5 flex-col tablet:flex-row tablet:gap-5 tablet:mb-25">
        <AdviceSection
          title="三大要素打造令人印象深刻的“关于我”"
          content={
            <>
              <p>三要素：职业定位、核心经历或能力、求职意向</p>
              <p>
                “关于我”模块在简历中的作用是用几行文字突出你的职业定位，并初步塑造职业形象。为了让这个部分令人印象深刻，可以从以下三个方面来构建内容：
              </p>
              <ul className="m-0 list-none">
                <li>
                  <strong>要素1：职业定位</strong>
                </li>
                <ul className="!ml-12 list-disc">
                  <li>
                    用一句话总结你的主要职业定位。这个定位应当明确且具有针对性，能够让用人单位快速理解你希望从事的工作类型。例如：“作为一名数据分析师，我专注于利用大数据来推动业务决策和优化。
                  </li>
                </ul>
              </ul>
            </>
          }
          sectionGroup={ABOUT_ME_EN}
          sectionIndex={1}
        />
        <AdviceSection
          title="如何在“关于我”中简明表达职业定位？"
          content={
            <>
              <p>方法：</p>
              <ul className="ml-5">
                <li>
                  <strong>首先明确职场角色：</strong>
                  职业定位应体现你在职场中的明确角色，它代表你对自身角色定位的理解;
                </li>
                <li>
                  <strong>其次匹配职业成长轨迹：</strong>
                  职业定位应与个人的职业成长轨迹高度一致，并反映你希望在职场上发展的方向。
                </li>
              </ul>
              <p className="mb-3">示例：</p>
            </>
          }
          sectionGroup={ABOUT_ME_EN}
          sectionIndex={2}
        />
        <AdviceSection
          title="如何在“关于我”模块中清晰呈现过往经验？"
          content={
            <>
              <p>方法：</p>
              <p className="ml-2">
                根据你的职业定位对过往经验做些许的精炼和拔高。
              </p>
              <p>
                <strong>精炼</strong>是指将繁杂的经历浓缩为几句核心内容，而
                <strong>拔高</strong>
                则意味着从更高的视角总结经验，以突出与你当前职业定位相关的关键能力。
              </p>
              <p className="mb-3">示例：</p>
            </>
          }
          sectionGroup={ABOUT_ME_EN}
          sectionIndex={3}
        />
      </div>
      <div className="flex justify-center gap-4 flex-col tablet:flex-row tablet:gap-5">
        <AdviceSection
          title="如何在“关于我”中精准传达求职意向？"
          content={
            <>
              <p>方法：</p>
              <p className="ml-2">
                首先表达职能和角色；其次确定领域和行业;
                最后描述工作环境和文化偏好
              </p>
              <p>示例：</p>
              <p>
                对于职场新人或应届生，如果你的职业目标尚未完全定型，可以采用较为概括性的表达方式。
              </p>
            </>
          }
          sectionGroup={ABOUT_ME_EN}
          sectionIndex={4}
        />
        <AdviceSection
          title="为什么“关于我”模块至关重要？"
          content={
            <ul className="!ml-5 list-disc">
              <li>
                <strong>具备个人特色的职业定位展示：</strong>
                <ul className="!ml-5 list-disc">
                  <li>
                    “关于我”模块是简历的开头部分，用于从个人视角出发，阐述你对职业定位的理解、经历概览以及求职意向。它的主观性很强，能够展示你希望塑造的职业形象。
                  </li>
                </ul>
              </li>
              <li>
                <strong>明确职业形象：</strong>
                <ul className="!ml-5 list-disc">
                  <li>
                    职业定位和求职意向：这个部分帮助用人单位快速了解你是谁、做过什么、想做什么。明确的职业定位和清晰的求职意向可以帮助用人单位更好地理解你是否适合目标岗位。
                  </li>
                </ul>
              </li>
            </ul>
          }
          sectionGroup={ABOUT_ME_EN}
          sectionIndex={5}
        />
        <AdviceSection
          title="求职意向太明确会影响求职机会吗？"
          content={
            <>
              <p>答案：</p>
              <p className="ml-2">明确的求职意向会提高工作机会匹配的上限</p>
              <ul className="!ml-5 list-disc">
                <li>
                  <strong>提高机会匹配的上限：</strong>
                  <ul className="!ml-5 list-disc">
                    <li>
                      如果你的期望与公司的需求高度一致，这会为你加分，并可能使你在竞争中脱颖而出。
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>不会降低机会的下限：</strong>
                </li>
              </ul>
            </>
          }
          sectionGroup={ABOUT_ME_EN}
          sectionIndex={6}
        />
      </div>
    </div>
  );
};

const EducationAdvice = (props: { hidden: boolean }) => {
  const { hidden } = props;

  return (
    <div hidden={hidden}>
      <div className="flex justify-center gap-4 mb-5 flex-col tablet:flex-row tablet:gap-5 tablet:mb-25">
        <AdviceSection
          title="怎样写出令人印象深刻的校园经历？"
          content={
            <>
              <p>方法：</p>
              <p className="font-semibold ml-2">
                以描述一个项目的方式来描述自己在校内的经历
              </p>
              <p>
                它的作用就是让缺少实习经历或者是正式工作经历的职场新人，有机会的来完整呈现自己的能力。做为一个职场新人，当你已经有目标的求职岗位以后，可以在校园经历中融入目标岗位的能力要求。
              </p>
            </>
          }
          sectionGroup={EDUCATION_EN}
          sectionIndex={1}
        />
        <AdviceSection
          title="一个完整的教育经历模块可以涵盖哪些内容？"
          content={
            <>
              <p>一个完整的教育经历模块可以分为以下三部分：</p>
              <ul className="!ml-5 list-disc">
                <li className="font-semibold">
                  学历、专业、课程和成绩、研究课题；
                </li>
                <li>奖项和证书；</li>
                <li>校内职务和社团活动；</li>
              </ul>
              <p>小贴士：</p>
              <ul className="!ml-5 list-disc">
                <li>
                  2和3可以视作是一个锦上添花的加分项，但若没有这部分内容也无需担心。
                </li>
              </ul>
            </>
          }
          sectionGroup={EDUCATION_EN}
          sectionIndex={2}
        />
        <AdviceSection
          title="有丰富工作经验的求职者如何写教育经历？"
          content={
            <ul className="!ml-5 list-disc">
              <li>
                <strong>教育经历的位置：</strong>
                <ul className="!ml-5 list-disc">
                  <li>
                    若你的工作经历更为亮眼（例如获得了显著的职业成就或担任了高管职位），应将教育经历放在工作经历之后，以突出工作经验。
                  </li>
                  <li>
                    若你的教育背景更为亮眼（例如来自全球TOP200或985/211高校），可以将教育经历放在简历前面，确保用人单位首先注意到你的学术背景。
                  </li>
                </ul>
              </li>
            </ul>
          }
          sectionGroup={EDUCATION_EN}
          sectionIndex={3}
        />
      </div>
      <div className="flex justify-center gap-4 flex-col tablet:flex-row tablet:gap-5">
        <AdviceSection
          title="应届生和职场新人应该如何写教育经历？"
          content={
            <>
              <p className="font-semibold ml-2">
                两个策略：详尽描述教育经历，和增加校园经历模块
              </p>
              <div>
                <p>用人单位在评估职场新人时，主要关注以下三方面的特质：</p>
                <ul className="!ml-5 list-disc">
                  <li>对岗位的热情</li>
                  <li>学习能力和意愿</li>
                  <li>基础素质</li>
                </ul>
              </div>
            </>
          }
          sectionGroup={EDUCATION_EN}
          sectionIndex={4}
        />
        <AdviceSection
          title="什么情况下要写校园经历？"
          content={
            <>
              <div>
                <p>
                  对于缺乏实习或正式工作经历的职场新人，校园经历可以帮助你展示在校期间所培养的能力：
                </p>
                <ul className="!ml-5 list-disc">
                  <li>执行能力：完成任务并做到位的能力。</li>
                  <li>组织协调能力：在团队中协作或牵头项目的能力。</li>
                  <li>
                    学习能力：在有效思考的基础上快速学习和适应新事物的能力。
                  </li>
                </ul>
              </div>
              <ul className="!ml-5 list-disc">
                <li>
                  <strong>没有任何实习经历或求职经历：</strong>
                  强烈建议增加校园经历模块，以弥补缺乏工作经验的不足。
                </li>
              </ul>
            </>
          }
          sectionGroup={EDUCATION_EN}
          sectionIndex={5}
        />
      </div>
    </div>
  );
};

const WorkExperienceAdvice = (props: { hidden: boolean }) => {
  const { hidden } = props;

  return (
    <div hidden={hidden}>
      <div className="flex justify-center gap-4 mb-5 flex-col tablet:flex-row tablet:gap-5 tablet:mb-25">
        <AdviceSection
          title="着笔之前，你应该了解岗位角色"
          content={
            <>
              <p>
                岗位角色是真正决定<strong>职业定位</strong>和
                <strong>工作价值</strong>的东西
              </p>
              <p className="">
                <strong>岗位角色：</strong>
                指的是你在某个职位上实际承担的内在职能和责任。它不仅仅是职位名称所代表的工作，而是你在这个职位中所扮演的具体角色和执行的核心任务。岗位角色决定了你的职业定位、工作价值以及核心能力要求，是影响职场晋升和发展的关键因素。
              </p>
            </>
          }
          sectionGroup={WORK_EXPERIENCE_EN}
          sectionIndex={1}
        />
        <AdviceSection
          title="如何以岗位角色为视角精准呈现自身能力？"
          content={
            <>
              <p>
                <strong>以岗位角色为视角来呈现自己的核心能力</strong>
                ，而不是以流水账的方式记录自己做了什么具体工作。
              </p>
              <ul className="m-0 list-none">
                <li>
                  <strong>第一步，明确自身岗位角色</strong>
                  <ul className="!ml-6 list-disc">
                    <li>
                      一般而言：协调者、执行者、项目把控者、团队管理者、业务负责人这几个层级
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>第二步， 梳理岗位角色与核心能力的关系</strong>
                </li>
              </ul>
            </>
          }
          sectionGroup={WORK_EXPERIENCE_EN}
          sectionIndex={2}
        />
        <AdviceSection
          title="一段完整的工作经历包含哪些内容？"
          content={
            <ul className="!ml-5 list-disc">
              <li>
                <strong>基础信息：</strong>公司名、职位名称、就职时间；
              </li>
              <li>
                <strong>整体概述：一句话，树立起你的角色定位；</strong>
              </li>
              <li>
                <strong>职责描述：</strong>
                就职期间主要负责的职责，一般使用项目符号，
                <strong>强化你的职业形象</strong>；
              </li>
              <li>
                <strong>工作成果：</strong>
                工作的具体成果，可以单独做为一个段落来讲，也可以合并放在职责描述里一起说，
                <strong>真实、客观地呈现与你角色相关的成果</strong>。
              </li>
            </ul>
          }
          sectionGroup={WORK_EXPERIENCE_EN}
          sectionIndex={3}
        />
      </div>
      <div className="flex justify-center gap-4 flex-col tablet:flex-row tablet:gap-5">
        <AdviceSection
          title="工作经历中的整体概述：梳理角色定位"
          content={
            <>
              <p>
                强调你所承担的责任和角色对整体项目或公司的影响力；确保概述中突出
                <strong>核心职责</strong>，而不是细节性工作。
              </p>
              <p>
                许多求职者常犯的错误是直接进入职责描述，而忽略了对岗位角色和主要责任的概述。如果缺少这一部分，特别是当你承担了管理职能或对最终结果负责时，错失展示自己核心价值的机会。
              </p>
            </>
          }
          sectionGroup={WORK_EXPERIENCE_EN}
          sectionIndex={4}
        />
        <AdviceSection
          title="工作经历中的职责描述：强化职业形象"
          content={
            <>
              <p>
                职责描述的关键在于<strong>强化你的职业定位和形象，</strong>
                而非简单记录你所做过的所有工作。
              </p>
              <p>以下几点可以帮助你有效强化职业形象：</p>
              <ul className="!ml-5 list-disc">
                <li>
                  <strong>能力导向，而非工作导向：</strong>
                  <ul className="!ml-5 list-disc">
                    <li>
                      描述工作职责时，不应仅从“我做过什么”的角度出发，而应围绕“
                      <strong>我具备哪些核心能力</strong>
                      ”展开。你需要在职责描述中清晰呈现出自己作为该岗位的核心技能与能力。例如，一个产品经理可以直接以
                      <strong>产品设计、产品管理和产品运营</strong>
                      等关键词作为职责描述的核心，展示出在这些方面的能力与经验。
                    </li>
                  </ul>
                </li>
              </ul>
            </>
          }
          sectionGroup={WORK_EXPERIENCE_EN}
          sectionIndex={5}
        />
        <AdviceSection
          title="工作经历中的工作成果：客观真实"
          content={
            <>
              <p>关键在于真实、客观地呈现与你角色相关的成果</p>
              <p>
                尽管许多求职者认为展示数字化成果非常重要，但其实，过度夸大或渲染反而可能降低你的职业形象。
              </p>
              <ul className="!ml-5 list-disc leading-6.5 text-black/85">
                <li className="mb-4">
                  <strong>真实呈现，避免夸大：</strong>
                  <ul className="!ml-5 list-disc">
                    <li>
                      在简历上，<strong>真实</strong>
                      的成果比夸张的数字和描述更能打动用人单位。当前市场上，许多求职者过于浮夸的自我包装已经引发了招聘方的警惕，因此建议你不必刻意渲染，只需如实呈现你的成果，这反而会让用人单位觉得你真实可信。
                    </li>
                  </ul>
                </li>
              </ul>
            </>
          }
          sectionGroup={WORK_EXPERIENCE_EN}
          sectionIndex={6}
        />
      </div>
    </div>
  );
};

const ProjectAdvice = (props: { hidden: boolean }) => {
  const { hidden } = props;

  return (
    <div hidden={hidden}>
      <div className="flex justify-center gap-4 mb-5 flex-col tablet:flex-row tablet:gap-5 tablet:mb-25">
        <AdviceSection
          title="写项目经历和写工作经历有什么区别？"
          content={
            <>
              <p>
                <strong>工作经历</strong>：展示“你做了什么”；
                <strong>项目经历</strong>：展示“你怎么做的”。
              </p>
              <ul className="!ml-5 list-disc">
                <li>
                  <strong>项目经历：</strong>
                  <ul className="!ml-5 list-disc">
                    <li>
                      <strong>作用：</strong>
                      呈现你具体的工作方法和执行过程，展示你在某一项工作中的思考与细节，帮助强化你的专业能力和执行力。
                    </li>
                  </ul>
                </li>
              </ul>
            </>
          }
          sectionGroup={PROJECT_EXPERIENCE_EN}
          sectionIndex={1}
        />
        <AdviceSection
          title="一个完整的项目经历包含哪些内容？"
          content={
            <ul className="!ml-5 list-disc">
              <li>
                <strong>基础信息：</strong>
                项目名称、担任的角色、项目时间、规模等；
              </li>
              <li>
                <strong>整体概述：</strong>
                简单的对项目背景和过程、价值成果等进行总结，一般由一小段话组成；
              </li>
              <li>
                <strong>项目描述：</strong>具体地描述项目开展过程和方法论；
              </li>
              <li>
                <strong>项目成果：</strong>总结项目具体成果；
              </li>
            </ul>
          }
          sectionGroup={PROJECT_EXPERIENCE_EN}
          sectionIndex={2}
        />
        <AdviceSection
          title="项目经历模块一般需要呈现几个项目？应如何挑选项目？"
          content={
            <>
              <p className="font-semibold">
                项目经历应精炼、重点突出，最多不要超过3个。
              </p>
              <ul className="!ml-5 list-disc">
                <li>
                  <strong>不同职级对应的项目数量建议</strong>
                  <ul className="!ml-5 list-disc">
                    <li>
                      <strong>高级管理者：</strong>
                      1个项目，或者不需要此模块。高级管理者更多依赖于其战略性决策和领导能力，而非具体项目的执行，因此项目经历的数量可以精简甚至忽略。
                    </li>
                  </ul>
                </li>
              </ul>
            </>
          }
          sectionGroup={PROJECT_EXPERIENCE_EN}
          sectionIndex={3}
        />
      </div>
      <div className="flex justify-center gap-4 flex-col tablet:flex-row tablet:gap-5">
        <AdviceSection
          title="写项目经历常犯的五个错误"
          content={
            <ul className="!ml-5 list-disc">
              <li>
                <strong>错误一：以写工作经历的方式来写项目经历</strong>
                <ul className="!ml-5 list-disc">
                  <li>
                    <strong>问题：</strong>
                    工作经历主要描述你在某个岗位上承担的职责和取得的成果，而项目经历应重点展示你如何执行具体项目。很多求职者不加区分，将项目经历简单当作工作职责的延伸，缺乏对项目执行过程的深入描述。
                  </li>
                </ul>
              </li>
            </ul>
          }
          sectionGroup={PROJECT_EXPERIENCE_EN}
          sectionIndex={4}
        />
        <AdviceSection
          title="没有实习和工作经历，如何完整展现你的能力？"
          content={
            <>
              <p className="font-semibold">
                极力推荐以<strong>项目</strong>
                的方式来展示自己在校内的工作和实践经历，将
                <strong>目标岗位</strong>
                的需求应该融入到校园经历中，
                <strong>确保所展示的能力与未来职业方向相关</strong>。
              </p>
              <p>
                对于缺乏实习或正式工作经验的职场新人，校园经历是展示能力的重要机会。当你有明确的求职目标时，可以在校园经历中融入目标岗位的能力要求。
              </p>
            </>
          }
          sectionGroup={PROJECT_EXPERIENCE_EN}
          sectionIndex={5}
        />
      </div>
    </div>
  );
};

const ResumeAdvice = () => {
  const [activeTab, setActiveTab] = React.useState(OVERVIEW_EN);
  const isMobile = useClintMediaQuery();

  return (
    <div
      className="my-20 px-5 tablet:my-25"
      data-ele-id="content"
      data-ele-group="landing_专业建议，扫清简历疑惑，助你精准表达优势"
      data-observer-id="landing_专业建议，扫清简历疑惑，助你精准表达优势"
    >
      <div className="text-center mb-6 tablet:mb-12">
        <p className="text-3.25xl font-semibold mb-3 tablet:text-4pxl">
          专业建议，扫清简历疑惑，助你精准表达优势
        </p>
      </div>
      <div className="flex justify-center mb-2 tablet:mb-12">
        <Tabs
          value={activeTab}
          onChange={(event, value) => {
            trackClickEvent(event);
            setActiveTab(value);
          }}
          className="rounded-full px-1 py-0.5 bg-slate-100 z-0"
          TabIndicatorProps={{
            className: '!bg-white !h-full rounded-full -z-10',
          }}
        >
          {ADVICE_MENU.slice(0, isMobile ? 3 : 5).map(({ title, en }) => (
            <Tab
              key={en}
              className="!px-10.25 !py-4 !text-base !text-[#2d2d2d] [&.Mui-selected]:!text-black [&.Mui-selected]:font-semibold"
              value={en}
              label={title}
              disableRipple={true}
              data-ele-group="resume-advice_tab"
              data-ele-id={`${en}_${title}`}
            />
          ))}
        </Tabs>
      </div>
      {isMobile && (
        <div className="flex justify-center mb-6 tablet:hidden">
          <Tabs
            value={activeTab}
            onChange={(event, value) => {
              trackClickEvent(event);
              setActiveTab(value);
            }}
            className="rounded-full px-1 py-0.5 bg-slate-100 z-0"
            TabIndicatorProps={{
              className: '!bg-white !h-full rounded-full -z-10',
            }}
          >
            {ADVICE_MENU.slice(3).map(({ title, en }) => (
              <Tab
                key={en}
                className="!px-10.25 !py-4 !text-base !text-[#2d2d2d] [&.Mui-selected]:!text-black [&.Mui-selected]:font-semibold"
                value={en}
                label={title}
                disableRipple={true}
                data-ele-group="resume-advice_tab"
                data-ele-id={`${en}_${title}`}
              />
            ))}
          </Tabs>
        </div>
      )}
      <OverViewAdvice hidden={activeTab !== OVERVIEW_EN} />
      <AboutMeAdvice hidden={activeTab !== ABOUT_ME_EN} />
      <EducationAdvice hidden={activeTab !== EDUCATION_EN} />
      <WorkExperienceAdvice hidden={activeTab !== WORK_EXPERIENCE_EN} />
      <ProjectAdvice hidden={activeTab !== PROJECT_EXPERIENCE_EN} />
    </div>
  );
};

export default ResumeAdvice;
